import { mapRenderOptions } from './helper';

// perform a very limited URL component encoding: just spaces for now. used
// only for dealing with cloudinary transforms, which are part of URLs in path
// info.
export function customEncodeURIComponent(val = '') {
  return val ? `${val}`.replace(/\s/g, '%20') : '';
}

// map service agnostic values to cloudinary values
//
// straight value mapping is done when value is string. when value is a
// function, the function returns an object since the effect of one
// service-agnostic render prop could be multiple service-specific render props.
//
// also, there may be some keys here that don't exist in renderOpsPropTypes.
// that's ok. we need to handle DEFAULT_RENDER_OPS which is service-specific.
//
// see API docs: https://cloudinary.com/documentation/image_transformation_reference
export const API_PROP_MAP = {
  width: 'w',
  height: 'h',
  format: 'f',
  quality: 'q',
  aspectRatio: 'ar',
  radius: 'r',

  // https://cloudinary.com/documentation/image_transformation_reference#crop_parameter
  resizeMode: val => {
    const map = {
      // Create an image with the exact given width and height while retaining
      // the original aspect ratio, using only part of the image that fills the
      // given dimensions if necessary (only part of the original image might be
      // visible if the requested aspect ratio is different from the original
      // aspect ratio).
      crop: { c: 'fill', g: 'auto' },

      // The image is resized so that it takes up as much space as possible
      // within a bounding box defined by the given width and height parameters.
      // The original aspect ratio is retained and all of the original image is
      // visible.
      scaleFit: { c: 'fit' },

      // Change the size of the image exactly to the given width and height
      // without necessarily retaining the original aspect ratio: all original
      // image parts are visible but might be stretched or shrunk.
      stretch: { c: 'scale' }
    };
    return map[val] || {};
  },

  // CLEANUP: once we start applying multiple effects, we're gonna have trouble
  // because the 'e' keys will overwrite each other when merged, so we kinda
  // gotta switch the mapped renderOps to an array of objects or something. :-/
  pixelate: val => ({ e: `pixelate:${val}` }),
  grayscale: val => (val === true ? { e: 'grayscale' } : {}),
  colorize: val => ({ e: `colorize:${val}` }),

  colorFill: val => ({ co: `rgb:${val}` }),

  // CLEANUP: if we ever implement more services, this will likely need to
  // change
  filter: val => ({ fl: val })
};

// all URLs will have this unless it's overridden by passed renderOptions
export const DEFAULT_RENDER_OPS = {
  format: 'auto', // serves optimal format to each user agent
  quality: 'auto:eco' // optimal quality via aggressive algorithm
};

/**
 * return provided params as kv pairs in pathInfo format (k1_v1,k2_v2,etc)
 *
 * note that we need to remove any undefined values because they are coerced
 * into strings with value 'undefined'
 *
 * @export
 * @param {Object} [params={}]
 * @returns {String} params as pathInfo k1_v1,k2_v2
 */
export function toPathInfo(params = {}) {
  return Object.keys(params)
    .reduce((acc, curr) => {
      const val = params[curr];
      return val === undefined
        ? acc
        : // cloudinary needs only spaces encoded for their transforms to
          // function correctly. the key will not have spaces, but just
          // encoding the whole thing for sanity.
          acc.concat(customEncodeURIComponent(`${curr}_${params[curr]}`));
    }, [])
    .join(',');
}

/**
 * returns a cloudinary URL for the image using the provided baseUrl as the
 * prefix.
 *
 * accepts renderOps object or array of renderOps, which is henceforth called
 * "transforms".
 *
 * @export
 * @param {String} baseUrl URL prefix for fetch or upload APIs
 * @param {String} publicId public id (which is a URL for fetch images)
 * @param {Object|Object[]} [options={}] service-agnostic render options (or an array of render options)
 * @param {String} existingTransforms one or more transforms in pathInfo form (e.g. 'c_crop,h_100' or 'c_crop,h_100/a_exif')
 * @returns {String} cloudinary image URL with translated renderOptions
 */
export function baseCreateUrl(
  baseUrl,
  publicId,
  options = {},
  existingTransforms
) {
  // normalize a single set of renderOps into a single transform.
  const transforms = Array.isArray(options) ? options : [options];

  // CLEANUP: factor this out to its own function
  const transformsAsPathInfo = transforms
    .map((opts, idx, arr) => {
      // we only want the DEFAULT_RENDER_OPS on the last transform since they
      // are focused on format and quality.
      const mergedOpts =
        idx === arr.length - 1
          ? Object.assign({}, DEFAULT_RENDER_OPS, opts)
          : opts;

      const params = mapRenderOptions(API_PROP_MAP, mergedOpts);

      return toPathInfo(params);
    })
    .join('/');

  // NOTE: filter(Boolean) removes empty strings from array, which toPathInfo
  // may return if mapped options are empty.
  return [
    baseUrl,
    customEncodeURIComponent(existingTransforms),
    transformsAsPathInfo,
    publicId
  ]
    .filter(Boolean)
    .join('/');
}
